import React, { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

/**
 * Modal de Instrucciones de Conexión y Código QR para Grito Mexicano
 * Permite al operador proyectar/mostrar el QR, copiar enlace directo o abrir la dinámica en nueva pestaña.
 */
export const PersonalNoisemeterInstructionsModal = ({
    isOpen,
    onClose,
    roomId = null,
    config = null
}) => {
    const [isCopied, setIsCopied] = useState(false);

    if (!isOpen) return null;

    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const roomParam = roomId ? `&roomId=${roomId}` : '';

    const qrUrl = `${baseUrl}/noisemeter?s=admin_qr${roomParam}`;
    const copyUrl = `${baseUrl}/noisemeter?s=admin_copy${roomParam}`;
    const directUrl = `${baseUrl}/noisemeter?s=admin_direct${roomParam}`;
    const rankingUrl = `${baseUrl}/noisemeter/ranking${roomId ? '?roomId=' + roomId : ''}`;

    const handleCopy = async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(copyUrl);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = copyUrl;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
            setIsCopied(true);
            setTimeout(() => {
                setIsCopied(false);
            }, 2000);
        } catch (err) {
            console.error('Error al copiar la URL:', err);
        }
    };

    const primaryColor = config?.primaryColor || '#006847';
    const logoSrc = config?.logoImageUrl || '/img/page/xolos-five-bicolor.png';

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    zIndex: 110000,
                    backdropFilter: 'blur(4px)'
                }}
            />

            {/* Modal Card Centrada */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '95%',
                    maxWidth: '540px',
                    zIndex: 110001
                }}
            >
                <div className="card shadow-lg border-0 rounded-4 overflow-hidden text-dark bg-white">
                    {/* Encabezado */}
                    <div
                        className="p-3 px-4 d-flex justify-content-between align-items-center text-white"
                        style={{ backgroundColor: primaryColor }}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-qr-code-scan fs-4"></i>
                            <div>
                                <h5 className="mb-0 fw-bold">¡Únete al Grito Mexicano!</h5>
                                <small className="opacity-75">Instrucciones de Conexión</small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            aria-label="Cerrar"
                        ></button>
                    </div>

                    {/* Cuerpo */}
                    <div className="card-body p-4 text-center">
                        <p className="text-secondary fw-medium mb-3">
                            Escanea el código QR con la cámara de tu celular para participar en la dinámica:
                        </p>

                        {/* Contenedor del QR */}
                        <div className="d-flex justify-content-center mb-3">
                            <div className="p-3 bg-white border rounded-4 shadow-sm d-inline-block">
                                <QRCodeCanvas
                                    value={qrUrl}
                                    size={220}
                                    level="H"
                                    includeMargin={true}
                                    imageSettings={{
                                        src: logoSrc,
                                        height: 44,
                                        width: 44,
                                        excavate: true
                                    }}
                                />
                            </div>
                        </div>

                        {/* Pasos rápidos */}
                        <div className="bg-light p-3 rounded-3 text-start small mb-3 border">
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <span className="badge rounded-pill bg-success">1</span>
                                <span className="text-dark">Apunta tu cámara al código QR o abre la URL.</span>
                            </div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <span className="badge rounded-pill bg-success">2</span>
                                <span className="text-dark">Ingresa tu nombre y teléfono para obtener tu ticket.</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge rounded-pill bg-success">3</span>
                                <span className="text-dark">¡Grita con toda la fuerza para entrar al ranking!</span>
                            </div>
                        </div>

                        {/* Caja de texto para copiar URL */}
                        <div className="mb-3 text-start">
                            <label className="form-label small fw-semibold text-secondary mb-1">
                                Enlace directo para participantes:
                            </label>
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control form-control-sm bg-light font-monospace"
                                    value={copyUrl}
                                    readOnly
                                    onClick={(e) => e.target.select()}
                                />
                                <button
                                    type="button"
                                    className={`btn btn-sm ${isCopied ? 'btn-success text-white' : 'btn-outline-primary'} fw-semibold d-flex align-items-center gap-1`}
                                    onClick={handleCopy}
                                >
                                    {isCopied ? (
                                        <>
                                            <i className="bi bi-check2"></i>
                                            <span>Copiado</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-clipboard"></i>
                                            <span>Copiar</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="card-footer bg-light p-3 px-4 d-flex justify-content-between align-items-center border-top">
                        <button
                            type="button"
                            className="btn btn-outline-info btn-sm px-3 fw-bold d-flex align-items-center gap-2"
                            onClick={() => window.open(rankingUrl, '_blank')}
                        >
                            <i className="bi bi-trophy-fill"></i>
                            <span>Ver pantalla de ranking</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-success btn-sm px-3 fw-bold text-white d-flex align-items-center gap-2"
                            onClick={() => window.open(directUrl, '_blank')}
                        >
                            <i className="bi bi-box-arrow-up-right"></i>
                            <span>Abrir dinámica en nueva pestaña</span>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PersonalNoisemeterInstructionsModal;
